import { useMemo, useRef, useCallback } from 'react';
import { getOrder } from 'app/apis/order';
import useSWR from 'swr';
import { CACHE_QUERY_KEY } from 'app/constants';
import { STATUS_CATEGORY } from 'app/base/orders/constants';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import debounce from 'lodash-es/debounce';

type GetOrderListFetcherProps = {
    status_category: STATUS_CATEGORY;
    page: number;
};

const getOrderListFetcher = async (params: GetOrderListFetcherProps) => {
    const res = await getOrder(params);

    if (res && 'data' in res) {
        return res;
    }

    return null;
};

export function useOrderList() {
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const pendingParamsRef = useRef<Record<string, string>>({});

    const currentTab = useMemo(() => {
        const tabParam = searchParams.get('tab') as STATUS_CATEGORY;

        return tabParam || STATUS_CATEGORY.UNPAID;
    }, [searchParams]);

    function getLatestSearchParams() {
        return new URLSearchParams(window.location.search);
    }

    const debounceRoutePush = useRef(
        debounce((replace: boolean = false) => {
            const currentParams = getLatestSearchParams();

            for (const [key, value] of Object.entries(pendingParamsRef.current)) {
                currentParams.set(key, value);
            }

            const act = replace ? router.replace : router.push;

            act(`${pathname}?${currentParams.toString()}`);

            pendingParamsRef.current = {};
        }, 100)
    ).current;

    const setCurrentTab = useCallback(
        (targetTab: STATUS_CATEGORY, replace: boolean = false) => {
            const TAB_KEY = 'tab';

            pendingParamsRef.current[TAB_KEY] = targetTab;

            debounceRoutePush(replace);
        },
        [debounceRoutePush]
    );

    const setCurrentPage = useCallback(
        (pageNumber: number, replace: boolean = false) => {
            const PAGE_KEY = 'p';

            const targetPage = pageNumber.toString();

            pendingParamsRef.current[PAGE_KEY] = targetPage;

            debounceRoutePush(replace);
        },
        [debounceRoutePush]
    );

    const currentPage = useMemo(() => {
        const pageParam = searchParams.get('p');
        const pageNumber = pageParam ? parseInt(pageParam) : 1;

        if (Number.isNaN(pageNumber) || pageNumber < 1) {
            return 1;
        }

        return pageNumber;
    }, [searchParams]);

    const { data: orderList, isLoading } = useSWR(
        [CACHE_QUERY_KEY.order, currentTab, currentPage],
        () => getOrderListFetcher({ status_category: currentTab, page: currentPage }),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            onError: () => {
                setCurrentPage(1, true);
                setCurrentTab(STATUS_CATEGORY.UNPAID, true);
            },
        }
    );

    const orderListData = orderList?.data;
    const pager = orderList?.pager;

    const state = {
        orderListData,
        pager,
        isLoading,
        currentPage,
        currentTab,
    };

    const action = {
        setCurrentPage,
        setCurrentTab,
    };

    return { state, action };
}
